/**
 * useAddHotspot — B8.1, erweitert um den Watch-Zweig (SPEC.md F1)
 *
 * useMutation gegen POST /api/v2/hotspots (Claim-Pfad, `watch` ist hier
 * immer false/undefined — setzt owned=true, durchlaeuft
 * authorize_hotspot_claim!/HotspotQuotaEnforcer).
 *
 * WP6 (Deep-Review 04.09.): `watch: true` ruft NICHT mehr POST + PATCH auf
 * (das setzte ueber den Claim-Pfad kurzzeitig owned=true, bevor PATCH
 * watched:true nachzog — dieselbe Zeile hatte danach owned=true UND
 * watched=true gleichzeitig, zaehlte gegen die Quota UND schlug bei fremden,
 * wallet-unverifizierten Hotspots mit 403 fehl). Stattdessen ruft `watch:
 * true` den neuen, quota-/claim-freien Endpunkt `POST
 * /api/v2/hotspots/:id/watch` auf, der laut WP-Text NUR
 * find_or_create_by(user, hotspot) mit owned:false, watched:true anlegt —
 * ohne authorize_hotspot_claim! und ohne HotspotQuotaEnforcer.
 *
 * ANNAHME (siehe Rueckgabetext des Auftrags): `:id` in der neuen Route wird
 * — analog zu resolve_hotspot_address im bestehenden #create — mit demselben
 * `name_or_address`-Wert befuellt, den auch der Claim-Pfad entgegennimmt
 * (Adresse ODER Name), NICHT zwingend mit der numerischen Hotspot-DB-ID. Der
 * FE-Aufrufer (AddHotspotModal.tsx) uebergibt an beiden Stellen (Suchtreffer
 * UND Freitext-Kandidat) ohnehin nur diesen String, nie `hit.id` — die neue
 * Route wird deshalb so aufgerufen, dass beide Faelle ohne Restrukturierung
 * weiterfunktionieren. Falls das Backend `:id` stattdessen als numerische
 * Hotspot-ID implementiert, muss dieser Hook auf `hit.id` (nur fuer
 * Suchtreffer verfuegbar) umgestellt werden.
 *
 * `POST /api/v2/hotspots/:id/watch` ist noch nicht im generierten
 * `@heliumtracker/api-types`-Paket enthalten (separates Build/Repo) — Aufruf
 * daher ueber eine lokale Typ-Assertion auf `apiClient.POST`, bis der
 * Contract dort nachgezogen wird (vgl. useReimportWallet.ts, wo
 * `/wallets/{id}/reimport` genauso zunaechst additiv ergaenzt wurde). Die
 * zentrale Auth-/401-Refresh-/Fehler-Middleware in lib/api-client.ts greift
 * unveraendert, da sie auf der HTTP-Response arbeitet, nicht auf dem
 * generierten Pfad-Typ.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

export type AddHotspotInput = {
  name_or_address: string
  /** true = Hotspot nur beobachten (read-only) statt aktiv zu betreiben. */
  watch?: boolean
}

type UntypedPostClient = (
  path: string,
  init: { params: { path: { id: string } } },
) => Promise<{ data?: unknown; error?: unknown }>

async function watchHotspot(nameOrAddress: string): Promise<unknown> {
  const post = apiClient.POST as unknown as UntypedPostClient
  const result = await post('/api/v2/hotspots/{id}/watch', {
    params: { path: { id: nameOrAddress } },
  })
  if (result.error) throw result.error
  return result.data
}

export function useAddHotspot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: AddHotspotInput) => {
      if (input.watch) {
        return watchHotspot(input.name_or_address)
      }

      const result = await apiClient.POST('/api/v2/hotspots', {
        body: { hotspot: { name_or_address: input.name_or_address } },
      })
      if (result.error) throw result.error
      return result.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hotspots'] })
      // Detail-Query ('hotspot', id) nach Claim eines fremden Hotspots neu laden (is_owner)
      void queryClient.invalidateQueries({ queryKey: ['hotspot'] })
    },
  })
}
